import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import { UserContext } from "../../../utils/contexts/UserContext";
import useModalsStore from "../../../utils/stores/useModalsStore";
import useOrgScopeStore from "../../../utils/stores/useOrgScopeStore";
import useDialog from "../../../utils/hooks/useDialog";
import DonutProgress from "../../DonutProgress/DonutProgress";
import Spinner from "../../Spinner/Spinner";
import StandardCard from "./StandardCard";
import { getActiveRubric } from "../../../services/rubricService";
import {
  getFocusAreas,
  getObservation,
  getObservations,
} from "../../../services/observationService";
import { getStaffOrganizations } from "../../../services/organizationService";
import { fullName } from "../../../utils/helpers/userDisplay";
import Avatar from "../../Avatar/Avatar";
import {
  OBSERVATION_PERMISSION_CODES,
  hasPermission,
} from "../../../utils/permissions";
import { formatShortDate } from "../../../utils/helpers/dateDisplay";
import { normalizeApiFocusAreas } from "../../../utils/helpers/focusAreas";
import { inferGradesFromObservations } from "../../../utils/helpers/teacherUtils";
import {
  buildOptionsMap,
  classifyAnswerWith,
} from "../../../utils/helpers/rubricAnswers";
import { scoreTierLegend } from "../../../utils/helpers/scoreColors";
import {
  COUNTED_OBSERVATION_LIMIT,
  COUNTED_WINDOW_DAYS,
  DISPLAYED_OBSERVATION_LIMIT,
  computeOverallScoreFromApi,
  computeStandardScoresFromDetails,
  selectCountedObservations,
} from "../../../utils/helpers/scoring";
import { FINALIZED_STATUSES } from "../../../utils/constants/observations";
import { standardsForTeacher } from "../../../utils/helpers/standardScope";
import { childrenIndex } from "../../../utils/helpers/orgTree";

const FINALIZED_STATUS_PARAM = [...FINALIZED_STATUSES].join(",");

const SCORE_LEGEND = scoreTierLegend();

export default function TeacherOverviewModal({ teacher, focusStandardUuid }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  // The school the viewer is drilled into (head-org staff browsing a child
  // school), else their own: the rubric's fallback.
  const selectedOrgUuid = useOrgScopeStore((s) => s.selectedOrgUuid);
  const organizations = useOrgScopeStore((s) => s.organizations);
  // A picked head or region rolls up schools that may each run their own
  // rubric, so only a picked SCHOOL decides the teacher's.
  const drilledSchoolUuid =
    selectedOrgUuid && !childrenIndex(organizations).has(selectedOrgUuid)
      ? selectedOrgUuid
      : null;
  const organizationUuid =
    selectedOrgUuid || user?.organizations?.[0]?.organization?.uuid || null;
  const close = useModalsStore((s) => s.closeTeacherOverview);

  const [observations, setObservations] = useState([]);
  // True finalized total from the paginated list envelope — observations
  // holds page 1 only, so its length caps at the backend page size.
  const [observationTotal, setObservationTotal] = useState(null);
  const [employmentStart, setEmploymentStart] = useState(null);
  const [rubric, setRubric] = useState(null);
  const [focusResult, setFocusResult] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [expandedStandardId, setExpandedStandardId] = useState(null);
  const [showFocusAreas, setShowFocusAreas] = useState(false);
  // Full detail of the last few finalized observations (most-recent-first),
  // for the per-question matrix, the per-question segment bars, and the
  // windowed per-standard scores.
  const [recentDetails, setRecentDetails] = useState([]);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);

  // Escape + focus trap + scroll lock + focus restore (one ref for whichever
  // of the two render branches below is mounted).
  const dialogRef = useDialog(close);

  useEffect(() => {
    if (!teacher?.uuid) {
      setIsLoading(false);
      return undefined;
    }
    // Drop the previous teacher's data so a quick teacher switch doesn't
    // briefly render the old teacher's bars, scores, or expanded grid under
    // the new teacher. Also honor focusStandardUuid (passed when the modal is
    // opened from a single-standard context) so that standard auto-expands.
    setObservations([]);
    setObservationTotal(null);
    setEmploymentStart(null);
    setRubric(null);
    setFocusResult(null);
    setExpandedStandardId(focusStandardUuid || null);
    setRecentDetails([]);
    setIsDetailsLoading(false);
    const controller = new AbortController();
    setIsLoading(true);
    setLoadError("");
    // Secondary data (focus areas, employment row) must not block the modal:
    // re-raise cancellation so the outer .catch handles it, swallow real
    // errors with a console.warn so a 500 isn't silent.
    const optional = (promise, label) =>
      promise.catch((err) => {
        if (err?.code === "ERR_CANCELED") throw err;
        // eslint-disable-next-line no-console
        console.warn(`${label} failed:`, err);
        return null;
      });
    const rubricOf = (params) =>
      getActiveRubric({ params, signal: controller.signal });
    // Read the teacher through the rubric of the school the viewer is drilled
    // into (then organizationUuid), else through THEIRS (?teacher=, the
    // version their focus areas use) — not the viewer's own: a head office or
    // a coordinator may run another version, scoped differently. The viewer's
    // is the fallback when the teacher is out of their reach or that rubric
    // lacks the standard the modal was opened on.
    const teacherRubric = async () => {
      if (!drilledSchoolUuid) {
        try {
          const rubricData = await rubricOf({ teacher: teacher.uuid });
          if (
            !focusStandardUuid ||
            rubricData?.standards?.some((s) => s.uuid === focusStandardUuid)
          ) {
            return rubricData;
          }
        } catch (err) {
          if (err?.code === "ERR_CANCELED") throw err;
        }
      }
      return rubricOf({ organization: organizationUuid });
    };
    Promise.all([
      getObservations({
        params: {
          observed_teacher__uuid: teacher.uuid,
          status__in: FINALIZED_STATUS_PARAM,
          ordering: "-observed_at",
        },
        signal: controller.signal,
      }).then((obsData) => {
        // Set as soon as it lands, so the detail fetches overlap the rest.
        if (controller.signal.aborted) return;
        setObservations(obsData?.results || []);
        setObservationTotal(obsData?.count ?? null);
      }),
      teacherRubric(),
      optional(
        getFocusAreas({
          params: { teacher: teacher.uuid },
          signal: controller.signal,
        }),
        "getFocusAreas",
      ),
      optional(
        getStaffOrganizations({
          params: { user: teacher.uuid, is_in_progress: true },
          signal: controller.signal,
        }),
        "getStaffOrganizations",
      ),
    ])
      .then(([, rubricData, focusData, staffData]) => {
        if (controller.signal.aborted) return;
        setRubric(rubricData);
        setFocusResult(focusData);
        // Rows are flat (organization is a uuid string); prefer the viewer's
        // organization, fall back to the most recent employment row.
        const staffRows = staffData?.results || [];
        const employmentRow =
          staffRows.find((r) => r.organization === organizationUuid) ||
          staffRows[0] ||
          null;
        setEmploymentStart(employmentRow?.start_date || null);
        setIsLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === "ERR_CANCELED") return;
        setLoadError(t("teacherModal.loadError"));
        setIsLoading(false);
      });
    return () => controller.abort();
  }, [teacher?.uuid, drilledSchoolUuid, organizationUuid, focusStandardUuid, t]);

  // Fetch the full detail of the last few finalized observations as soon as
  // the list loads — the per-question answers feed the expanded matrix and the
  // segment bars, and standard_scores feed the windowed per-standard averages.
  // Runs once per teacher (observations changes on teacher switch); cancels
  // cleanly on switch / modal unmount.
  useEffect(() => {
    const recent = observations.slice(0, DISPLAYED_OBSERVATION_LIMIT);
    if (recent.length === 0) {
      setRecentDetails([]);
      return undefined;
    }
    const controller = new AbortController();
    setIsDetailsLoading(true);
    Promise.all(
      recent.map((o) =>
        getObservation({ uuid: o.uuid, signal: controller.signal }).catch(
          (err) => {
            if (err?.code === "ERR_CANCELED") throw err;
            return null;
          },
        ),
      ),
    )
      .then((details) => {
        if (controller.signal.aborted) return;
        setRecentDetails(details.filter(Boolean));
        setIsDetailsLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === "ERR_CANCELED") return;
        setRecentDetails([]);
        setIsDetailsLoading(false);
      });
    return () => controller.abort();
  }, [observations]);

  const standards = useMemo(
    () =>
      [...(rubric?.standards || [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0),
      ),
    [rubric],
  );

  const classifyAnswer = useMemo(
    () => classifyAnswerWith(buildOptionsMap(rubric)),
    [rubric],
  );

  // The counted window — single source of truth from scoring.js, shared with
  // the teacher's own dashboard so manager and teacher see the same numbers.
  const countedObservations = useMemo(
    () => selectCountedObservations(observations),
    [observations],
  );
  // Column fade flags must agree with the per-standard averages, which are
  // computed over the fetched detail payloads — so derive both from
  // recentDetails (a counted observation whose detail fetch failed slides the
  // window for both, never for just one).
  const countedUuids = useMemo(
    () => new Set(selectCountedObservations(recentDetails).map((o) => o.uuid)),
    [recentDetails],
  );

  const overallScore = useMemo(() => {
    const raw = computeOverallScoreFromApi(observations);
    return raw == null ? null : Math.round(raw);
  }, [observations]);

  const standardScores = useMemo(
    () => computeStandardScoresFromDetails(recentDetails, standards),
    [recentDetails, standards],
  );

  // When the modal is opened from a single standard (StandardDetail funnel,
  // a per-standard dot, a heatmap cell), scope the body to just that standard;
  // otherwise to the ones this teacher's lessons are scored on.
  const visibleStandards = useMemo(
    () =>
      focusStandardUuid
        ? standards.filter((s) => s.uuid === focusStandardUuid)
        : standardsForTeacher(
            standards,
            (teacher?.subjects || []).map((s) => s.uuid),
            standardScores,
          ),
    [standards, focusStandardUuid, teacher?.subjects, standardScores],
  );

  // Matrix columns oldest → newest; stale columns render dimmed.
  const columns = useMemo(
    () =>
      [...recentDetails]
        .reverse()
        .map((detail) => ({
          detail,
          isCounted: countedUuids.has(detail.uuid),
        })),
    [recentDetails, countedUuids],
  );

  const focusAreas = useMemo(
    () => normalizeApiFocusAreas(focusResult),
    [focusResult],
  );

  const grades = useMemo(
    () => inferGradesFromObservations(observations),
    [observations],
  );

  const lastObservation = observations[0]?.observed_at;
  // The newest observation has the max observed_at, so the counted set is
  // non-empty exactly when the newest one is inside the window.
  const isLastObservationFresh = countedObservations.length > 0;
  const lessonsObserved = observationTotal ?? observations.length;

  const toggleExpand = (standardUuid) => {
    setExpandedStandardId((prev) => (prev === standardUuid ? null : standardUuid));
  };

  if (!teacher?.uuid) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
        onClick={close}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm text-center focus:outline-none"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-sm text-slate-500">
            {t("teacherModal.notAvailable")}
          </p>
          <button
            type="button"
            onClick={close}
            className="mt-4 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-bold text-slate-700"
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    );
  }

  const teacherName = teacher.full_name || fullName(teacher);
  const subjects = (teacher.subjects || []).map((s) => s.name).join(", ");
  const department = teacher.department || null;
  const employedSinceYear = employmentStart
    ? new Date(employmentStart).getFullYear()
    : null;

  const headerStats = [
    {
      label: t("teacher.employedSince"),
      value: employedSinceYear || "—",
      title: employmentStart ? formatShortDate(employmentStart) : undefined,
    },
    {
      label: t("teacher.subject"),
      value: subjects || "—",
      title: subjects || undefined,
    },
    {
      label: t("teacher.grades"),
      value: grades || "—",
    },
    {
      label: t("teacher.lastObservation"),
      value: lastObservation ? formatShortDate(lastObservation) : "—",
      valueClass: lastObservation
        ? isLastObservationFresh
          ? "text-emerald-600"
          : "text-amber-600"
        : undefined,
      title:
        lastObservation && !isLastObservationFresh
          ? t("teacherModal.notCounted")
          : undefined,
    },
    {
      label: t("teacher.lessonsObserved"),
      value: lessonsObserved,
      valueClass: "text-indigo-600",
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={close}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className="bg-white rounded-[32px] shadow-2xl w-full max-w-4xl h-[85vh] overflow-hidden flex flex-col focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-8 pb-6 border-b border-slate-100 shrink-0 bg-slate-50/30">
          <div className="flex justify-between items-start gap-8 mb-6">
            <div className="flex items-center gap-6 min-w-0">
              <Avatar
                user={teacher}
                alt={teacherName}
                className="w-20 h-20 rounded-full bg-slate-900 text-white text-2xl font-black shrink-0 ring-1 ring-slate-200 shadow-xl"
              />
              <div className="min-w-0">
                <h3 className="text-2xl font-black text-slate-900 tracking-tight truncate">
                  {teacherName}
                </h3>
                {department && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="px-3 py-0.5 text-[10px] font-black uppercase tracking-wider rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                      {department}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-6 shrink-0">
              {overallScore != null && (
                <div className="flex flex-col items-center">
                  <DonutProgress
                    score={overallScore}
                    size={72}
                    strokeWidth={7}
                    labelSize="text-sm"
                  />
                  <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest mt-1">
                    {t("teacher.overall")}
                  </span>
                </div>
              )}
              <div className="flex flex-col gap-2">
                {hasPermission(user, OBSERVATION_PERMISSION_CODES.CREATE) && (
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      navigate(
                        `/observation?teacher=${encodeURIComponent(teacher.uuid)}`,
                      );
                    }}
                    className="bg-slate-900 hover:bg-black text-white px-5 py-2.5 rounded-2xl font-bold text-sm shadow-xl shadow-slate-900/20 whitespace-nowrap"
                  >
                    {t("teacherModal.newObservation")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={close}
                  className="bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-800 px-5 py-2 rounded-2xl font-bold text-sm border border-slate-200 whitespace-nowrap"
                >
                  {t("common.close")}
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-4">
            {headerStats.map((stat) => (
              <div key={stat.label} className="min-w-0">
                <p
                  className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1 truncate"
                  title={stat.label}
                >
                  {stat.label}
                </p>
                <p
                  className={`text-sm font-bold truncate ${stat.valueClass || "text-slate-800"}`}
                  title={
                    stat.title ||
                    (typeof stat.value === "string" ? stat.value : undefined)
                  }
                >
                  {stat.value}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-8 pt-6 space-y-6 bg-slate-50/50">
          {isLoading ? (
            <Spinner label={t("profile.loading")} className="h-full" />
          ) : loadError ? (
            <div className="h-full flex items-center justify-center text-sm text-rose-600">
              {loadError}
            </div>
          ) : observations.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">
              {t("teacherModal.noObservations")}
            </div>
          ) : (
            <>
              {/* Focus Areas — cross-standard summary, hidden when the modal
                  is scoped to a single standard. */}
              {!focusStandardUuid && (
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowFocusAreas(!showFocusAreas)}
                  className="w-full p-5 flex items-center justify-between hover:bg-slate-50"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-rose-50 text-rose-500 rounded-xl flex items-center justify-center">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2.5"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        {t("teacher.suggestedFocusAreas")}
                      </p>
                      <p className="text-sm font-bold text-slate-900">
                        {t("teacherModal.itemsIdentified", { count: focusAreas.length })}
                      </p>
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-slate-400 transition-transform ${showFocusAreas ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                <AnimatePresence>
                  {showFocusAreas && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden border-t border-slate-100"
                    >
                      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {focusAreas.length > 0 ? (
                          focusAreas.map((area) => (
                            <div
                              key={area.key}
                              className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-start gap-3"
                            >
                              <div className="w-8 h-8 bg-white border border-slate-200 rounded-lg flex items-center justify-center shrink-0 text-[9px] font-black text-slate-500">
                                S{area.domainNumber}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <span className="text-[9px] font-black text-indigo-600 uppercase tracking-widest truncate">
                                    {area.domainTitle}
                                  </span>
                                  <span className="text-[8px] font-bold text-rose-500 bg-rose-50 px-1.5 py-0.5 rounded-full uppercase tracking-tighter shrink-0">
                                    {area.count}×
                                  </span>
                                </div>
                                <p className="text-[11px] text-slate-600 leading-snug line-clamp-2">
                                  {area.fullText}
                                </p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="py-6 text-center col-span-2 text-xs text-slate-400">
                            {t("teacher.focusEmptyNone")}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              )}

              {/* Standards */}
              <div className="space-y-3">
                {visibleStandards.length === 0 && (
                  <div className="text-center text-sm text-slate-400 py-8">
                    {t("teacherModal.noRubric")}
                  </div>
                )}
                {visibleStandards.map((std) => {
                  const raw = standardScores[std.uuid];
                  return (
                    <StandardCard
                      key={std.uuid}
                      std={std}
                      versionUuid={rubric?.uuid}
                      score={raw == null ? null : Math.round(raw)}
                      isExpanded={expandedStandardId === std.uuid}
                      onToggle={() => toggleExpand(std.uuid)}
                      columns={columns}
                      classifyAnswer={classifyAnswer}
                      isDetailsLoading={isDetailsLoading}
                    />
                  );
                })}
              </div>

              {/* Scoring-window note + score-tier legend */}
              <div className="pt-6 border-t border-slate-100 space-y-3">
                <p className="text-center text-[10px] font-bold text-slate-500">
                  {t("teacherModal.statsWindowNote", {
                    count: COUNTED_OBSERVATION_LIMIT,
                    days: COUNTED_WINDOW_DAYS,
                  })}
                </p>
                <div className="flex items-center justify-center gap-5 flex-wrap text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  {SCORE_LEGEND.map((tier) => (
                    <span key={tier.min} className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${tier.dotClass}`} />{" "}
                      {tier.label}
                    </span>
                  ))}
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-slate-400 opacity-40" />{" "}
                    {t("teacherModal.legendNotCounted")}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
